import {
  Component,
  Input,
  ViewEncapsulation,
  ChangeDetectorRef,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  CompactType,
  DisplayGrid,
  Gridster,
  GridsterConfig,
  GridsterItem,
  GridsterItemConfig,
  GridType,
} from 'angular-gridster2';
import { DynamicComponentComponent } from '../dynamic-component/dynamic-component.component';
import { TemplateGeneratorComponent } from '../template-generator/template-generator.component';
import { ModalService } from '../../services/modal/modal.service';
import { CustomModal } from '../../models/CustomModal.model';
import { CustomComponent } from '../../models/CustomComponent.model';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ComponentPortal } from '@angular/cdk/portal';
import { EditComponentModalComponent } from '../modals/edit-component-modal/edit-component-modal.component';
import { ComponentRegistryService } from '../../services/component-registry/component-registry.service';
import { DragService } from '../../services/drag/drag.service';
import { OrderByPipe } from '../../pipes/order-by.pipe';

@Component({
  selector: 'app-template-generator-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    Gridster,
    GridsterItem,
    DynamicComponentComponent,
    MatTabGroup,
    MatTab,
    OrderByPipe,
  ],
  templateUrl: './template-generator-modal.component.html',
  styleUrl: './template-generator-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class TemplateGeneratorModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() templateGeneratorComponent!: TemplateGeneratorComponent;
  selectedCustomComponentName: string | null = null;
  selectedModalUUID: string | null = null;
  savedModals: CustomModal[] = [];
  currentMode: 1 | 2 | 3 = 1;
  gridOptions: GridsterConfig = {};
  copyComponentsFromMode_mode: 1 | 2 | 3 = 1;

  private dragStartListener: ((event: Event) => void) | null = null;
  private dragEndListener: ((event: Event) => void) | null = null;

  constructor(
    templateGeneratorComponent: TemplateGeneratorComponent,
    private modalService: ModalService,
    private changeDetectorRef: ChangeDetectorRef,
    private componentRegistry: ComponentRegistryService,
    private dragService: DragService
  ) {
    this.templateGeneratorComponent = templateGeneratorComponent;
    this.loadModals();
  }

  ngOnInit(): void {
    this.gridOptions = {
      gridType: GridType.VerticalFixed,
      compactType: CompactType.None,
      margin: 0,
      outerMargin: false,
      draggable: {
        enabled: true,
        ignoreContent: true,
        dragHandleClass: 'drag-handle',
        ignoreContentClass: 'content-area',
      },
      resizable: {
        enabled: true,
      },
      pushItems: false,
      pushResizeItems: false,
      swap: false,
      minCols: 12,
      maxCols: 12,
      minItemRows: 1,
      maxItemRows: 50,
      minItemCols: 1,
      maxItemCols: 12,
      minRows: 30,
      maxRows: Infinity,
      rowHeight: 50,
      fixedRowHeight: 50,
      setGridSize: true,
      useTransformPositioning: true,
      mobileBreakpoint: 10,
      displayGrid: DisplayGrid.Always,
      enableEmptyCellClick: false,
      emptyCellDrag: false,
      keepFixedHeightInMobile: true,
      keepFixedWidthInMobile: false,
      enableOccupiedCellDrop: true,
      dragEnabled: true,
      dragAndDrop: true,
      enableEmptyCellDrop: true,
      itemChangeCallback: (item: GridsterItemConfig) => {
        this.changeDetectorRef.detectChanges();
      },
      itemResizeCallback: (item: GridsterItemConfig) => {
        this.changeDetectorRef.detectChanges();
      },
      optionsChanged: () => {
        this.changeDetectorRef.detectChanges();
      },
    };

    // Listen for component dropped events from containers
    document.addEventListener('componentDropped', ((event: CustomEvent) => {
      const { sourceUUID, containerUUID, x, y, mode } = event.detail;

      // Find the component in the modal's components
      const modal = this.getSelectedModal();
      if (!modal) return;

      const findComponent = (
        components: CustomComponent[]
      ): CustomComponent | null => {
        for (const comp of components) {
          if (comp.uuid === sourceUUID) return comp;
          if (comp.childComponents?.length) {
            const found = findComponent(comp.childComponents);
            if (found) return found;
          }
        }
        return null;
      };

      const component = findComponent(modal.customComponents);
      if (!component) {
        console.error('Component not found:', sourceUUID);
        return;
      }

      // Remove component from its current parent
      const removeFromParent = (components: CustomComponent[]): boolean => {
        const index = components.findIndex((c) => c.uuid === sourceUUID);
        if (index !== -1) {
          components.splice(index, 1);
          return true;
        }
        for (const comp of components) {
          if (comp.childComponents?.length) {
            if (removeFromParent(comp.childComponents)) return true;
          }
        }
        return false;
      };

      removeFromParent(modal.customComponents);

      // Find target container
      const findContainer = (
        components: CustomComponent[]
      ): CustomComponent | null => {
        for (const comp of components) {
          if (comp.uuid === containerUUID) return comp;
          if (comp.childComponents?.length) {
            const found = findContainer(comp.childComponents);
            if (found) return found;
          }
        }
        return null;
      };

      const targetContainer =
        containerUUID === this.selectedModalUUID
          ? null
          : findContainer(modal.customComponents);

      // Update component properties
      component.x = x;
      component.y = y;
      component.mode = mode;
      component.parentUUID = containerUUID;

      // Add to new parent
      if (targetContainer) {
        if (!targetContainer.childComponents) {
          targetContainer.childComponents = [];
        }
        targetContainer.childComponents.push(component);
      } else {
        modal.customComponents.push(component);
      }

      this.changeDetectorRef.detectChanges();
      this.saveModal();
    }) as EventListener);
  }

  ngAfterViewInit() {
    // Initialize drag zone activation
    this.dragStartListener = () => {
      document.querySelectorAll('.gridster-drop-zone').forEach((zone) => {
        zone.classList.add('dragging-active');
      });

      // Ensure component container drop zones are active
      document
        .querySelectorAll('app-component-container .gridster-drop-zone')
        .forEach((zone) => {
          zone.classList.add('dragging-active');
        });
    };

    this.dragEndListener = () => {
      document.querySelectorAll('.gridster-drop-zone').forEach((zone) => {
        zone.classList.remove('dragging-active');
        zone.classList.remove('drag-over');
      });

      // Clean up component container drop zones
      document
        .querySelectorAll('app-component-container .gridster-drop-zone')
        .forEach((zone) => {
          zone.classList.remove('dragging-active');
          zone.classList.remove('drag-over');
        });
    };

    document.addEventListener('dragstart', this.dragStartListener);
    document.addEventListener('dragend', this.dragEndListener);

    // Initialize component containers
    this.initializeComponentContainers();
  }

  private initializeComponentContainers() {
    // Set up mutation observer to watch for new component containers
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              const containers = node.querySelectorAll(
                'app-component-container'
              );
              containers.forEach((container) => {
                const dropZone = container.querySelector('.gridster-drop-zone');
                if (dropZone) {
                  dropZone.classList.add('nested');
                }
              });
            }
          });
        }
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, { childList: true, subtree: true });
  }

  ngOnDestroy() {
    // Clean up event listeners
    if (this.dragStartListener) {
      document.removeEventListener('dragstart', this.dragStartListener);
    }
    if (this.dragEndListener) {
      document.removeEventListener('dragend', this.dragEndListener);
    }
    // Clean up componentDropped listener
    document.removeEventListener(
      'componentDropped',
      (() => {}) as EventListener
    );
  }

  onModeChange(event: any) {
    this.currentMode = (event.index + 1) as 1 | 2 | 3;
    this.updateGridsterSize();
  }

  public updateGridsterSize() {
    const modal = this.getSelectedModal();
    if (modal) {
      const gridsterElement = document.querySelector(
        '.modal-preview gridster'
      ) as HTMLElement;
      if (gridsterElement) {
        gridsterElement.style.width =
          modal.settings['width'].value[this.currentMode];
        gridsterElement.style.height =
          modal.settings['height'].value[this.currentMode];
        window.dispatchEvent(new Event('resize'));
      }
    }
  }

  getSelectedModalComponents(): CustomComponent[] {
    const modal = this.getSelectedModal();
    return modal ? modal.getComponentsForMode(this.currentMode) : [];
  }

  loadModals() {
    this.modalService.getModals().subscribe((modals) => {
      this.savedModals = modals;
    });
  }

  saveModal() {
    const modal = this.savedModals.find(
      (m) => m.uuid === this.selectedModalUUID
    );
    if (modal) {
      this.modalService.postModal(modal).subscribe(
        (savedModal) => {
        },
        (error) => {
          console.error('Error saving modal:', error);
        }
      );
    }
  }

  saveModalAsNew() {
    const modalName = prompt('Enter a name for the new modal');
    if (modalName) {
      const newModal = new CustomModal(modalName);
      this.savedModals.push(newModal);
      this.selectedModalUUID = newModal.uuid;
      this.saveModal();
    }
  }

  getSelectedModal(): CustomModal | null {
    return (
      this.savedModals.find((m) => m.uuid === this.selectedModalUUID) || null
    );
  }

  editComponent(uuid: string) {
    const modal = this.getSelectedModal();
    if (!modal) {
      console.error('No modal selected');
      return;
    }

    const component = this.findComponentInModal(modal, uuid);
    if (!component) {
      console.error('Component not found in modal:', uuid);
      return;
    }

    // Create a deep copy of the component for editing
    const editedComponent = Object.create(
      Object.getPrototypeOf(component),
      Object.getOwnPropertyDescriptors(component)
    );

    // Ensure component class is set
    if (editedComponent && !editedComponent.component) {
      const registeredComponent =
        this.templateGeneratorComponent.componentRegistry.getComponentByName(
          editedComponent.name
        );
      if (registeredComponent?.component) {
        editedComponent.component = registeredComponent.component;
      }
    }

    // Create and show the edit modal
    const positionStrategy = this.templateGeneratorComponent.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayRef = this.templateGeneratorComponent.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      panelClass: 'edit-modal-overlay',
    });

    const portal = new ComponentPortal(EditComponentModalComponent);
    const componentRef = overlayRef.attach(portal);

    componentRef.instance.isOpen = true;
    componentRef.instance.component = editedComponent;

    // Handle save and close
    componentRef.instance.saveChanges.subscribe(() => {
      this.saveComponentChanges(editedComponent);
      overlayRef.dispose();
    });

    componentRef.instance.closeModal.subscribe(() => {
      overlayRef.dispose();
    });

    overlayRef.backdropClick().subscribe(() => {
      overlayRef.dispose();
    });
  }

  private findComponentInModal(
    modal: CustomModal,
    uuid: string
  ): CustomComponent | null {
    const findRecursive = (
      components: CustomComponent[]
    ): CustomComponent | null => {
      for (const comp of components) {
        if (comp.uuid === uuid) return comp;
        if (comp.childComponents?.length) {
          const found = findRecursive(comp.childComponents);
          if (found) return found;
        }
      }
      return null;
    };

    return findRecursive(modal.getComponentsForMode(this.currentMode));
  }

  private saveComponentChanges(editedComponent: CustomComponent) {
    const modal = this.getSelectedModal();
    if (!modal) return;

    // Helper function to find and update component recursively
    const updateComponentRecursive = (
      components: CustomComponent[]
    ): boolean => {
      const index = components.findIndex(
        (c) => c.uuid === editedComponent.uuid
      );
      if (index !== -1) {
        components[index] = editedComponent;
        return true;
      }

      // Search in child components
      for (const comp of components) {
        if (comp.childComponents?.length) {
          if (updateComponentRecursive(comp.childComponents)) {
            return true;
          }
        }
      }
      return false;
    };

    if (updateComponentRecursive(modal.customComponents)) {
      this.changeDetectorRef.detectChanges();
      this.saveModal();
    } else {
      console.error('Component not found for update:', editedComponent.uuid);
    }
  }

  removeComponent(uuid: string) {
    const modal = this.getSelectedModal();
    if (!modal) {
      console.error('No modal selected');
      return;
    }

    if (confirm('Er du sikker på at du vil fjerne denne component?')) {
      // Find and remove the component directly from customComponents
      const removeRecursive = (components: CustomComponent[]): boolean => {
        const index = components.findIndex((c) => c.uuid === uuid);
        if (index !== -1) {
          components.splice(index, 1);
          return true;
        }

        // Search in child components
        for (const comp of components) {
          if (comp.childComponents?.length) {
            if (removeRecursive(comp.childComponents)) {
              return true;
            }
          }
        }
        return false;
      };

      if (removeRecursive(modal.customComponents)) {
        this.changeDetectorRef.detectChanges();
        this.saveModal();
      } else {
        console.error('Component not found for removal:', uuid);
      }
    }
  }

  copyComponent(component: CustomComponent) {
    const modal = this.getSelectedModal();
    if (!modal) {
      console.error('No modal selected');
      return;
    }

    // Deep clone function for components
    const deepCloneComponent = (comp: CustomComponent): CustomComponent => {
      // Deep clone settings and remove customID
      const clonedSettings = comp.settings
        ? JSON.parse(JSON.stringify(comp.settings))
        : undefined;
      if (clonedSettings && 'customID' in clonedSettings) {
        clonedSettings['customID'] = { type: 'string', value: '' };
      }

      // Create new component instance
      const clonedComp = new CustomComponent(
        comp.name,
        comp.x,
        comp.y,
        comp.cols,
        comp.rows,
        comp.customCSS,
        clonedSettings,
        comp.inputs ? JSON.parse(JSON.stringify(comp.inputs)) : undefined,
        comp.mode
      );

      // Recursively clone children if they exist
      if (comp.childComponents && comp.childComponents.length > 0) {
        clonedComp.childComponents = comp.childComponents.map((child) => {
          const clonedChild = deepCloneComponent(child);
          clonedChild.parentUUID = clonedComp.uuid; // Set parent UUID to new parent's UUID
          return clonedChild;
        });
      }

      return clonedComp;
    };

    // Create the deep copy with new UUIDs
    const componentCopy = deepCloneComponent(component);

    // Adjust position slightly to make it visible it's a copy
    componentCopy.x = (component.x || 0) + 1;
    componentCopy.y = (component.y || 0) + 1;

    // Add the copied component to the modal's components
    modal.customComponents.push(componentCopy);

    // Force change detection
    this.changeDetectorRef.detectChanges();
    this.saveModal();
  }

  copyComponentsFromMode(toMode: 1 | 2 | 3) {
    const modal = this.getSelectedModal();
    if (!modal) {
      console.error('No modal selected');
      return;
    }

    // Use the renamed property here
    const sourceComponents = modal.getComponentsForMode(
      this.copyComponentsFromMode_mode
    );
    if (!sourceComponents.length) {
      return;
    }

    // Deep clone function for components
    const deepCopyComponent = (comp: CustomComponent): CustomComponent => {
      // Deep clone settings
      const clonedSettings = comp.settings
        ? JSON.parse(JSON.stringify(comp.settings))
        : undefined;
      if (clonedSettings && 'customID' in clonedSettings) {
        clonedSettings['customID'] = { type: 'string', value: '' };
      }

      // Create new component instance
      const clonedComp = new CustomComponent(
        comp.name,
        comp.x,
        comp.y,
        comp.cols,
        comp.rows,
        comp.customCSS,
        clonedSettings,
        comp.inputs ? JSON.parse(JSON.stringify(comp.inputs)) : undefined,
        toMode // Use the passed toMode instead of currentMode
      );

      // Recursively clone children if they exist
      if (comp.childComponents && comp.childComponents.length > 0) {
        clonedComp.childComponents = comp.childComponents.map((child) => {
          const clonedChild = deepCopyComponent(child);
          clonedChild.parentUUID = clonedComp.uuid;
          return clonedChild;
        });
      }

      return clonedComp;
    };

    // Create deep copies of all components with the new mode
    const copiedComponents = sourceComponents.map((component) =>
      deepCopyComponent(component)
    );

    // Remove existing components in the target mode
    modal.customComponents = modal.customComponents.filter(
      (comp) => comp.mode !== toMode
    );

    // Add the copied components
    modal.customComponents.push(...copiedComponents);

    // Force change detection and save
    this.changeDetectorRef.detectChanges();
    this.saveModal();
  }

  onDragStart(event: DragEvent, item: CustomComponent) {

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      const dragData = {
        ...CustomComponent.convertToDTO(item),
        componentName: item.name,
        sourceUUID: item.uuid,
        parentUUID: this.selectedModalUUID,
        isComponentContainer: item.name === 'ComponentContainerComponent',
        isChild: item.parentUUID !== undefined,
        childComponents: item.childComponents?.map((child) =>
          CustomComponent.convertToDTO(child)
        ),
      };

      event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
      this.dragService.setDragData(dragData);
    }
  }

  onDragEnd(event: DragEvent) {
    this.dragService.clearDragData();
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone?.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone?.classList.remove('drag-over');
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const dragData = this.dragService.getDragData();
    if (!dragData) {
      return;
    }

    // Get the actual target container UUID
    const targetElement = event.target as HTMLElement;
    let targetContainerUUID = targetElement.getAttribute('data-container-uuid');

    // If no direct container UUID found, look for the closest container
    if (!targetContainerUUID) {
      const container = targetElement.closest('[data-container-uuid]');
      targetContainerUUID =
        container?.getAttribute('data-container-uuid') || '';
    }

    // If still no container UUID, assume it's the main modal container
    if (!targetContainerUUID) {
      targetContainerUUID = this.selectedModalUUID!;
    }

    // Prevent dropping on itself or its current parent
    if (
      dragData.uuid === targetContainerUUID ||
      (dragData.parentUUID === targetContainerUUID &&
        targetContainerUUID !== this.selectedModalUUID)
    ) {
      return;
    }

    // Move the component
    this.moveWithinContainer(dragData, targetContainerUUID, event);
  }

  private moveWithinContainer(
    dragData: any,
    targetContainerUUID: string,
    event: DragEvent
  ) {

    const targetElement = event.target as HTMLElement;
    const container = document.querySelector(
      `[data-container-uuid="${targetContainerUUID}"]`
    );
    const rect = container?.getBoundingClientRect();
    const x = rect
      ? Math.floor((event.clientX - rect.left) / (rect.width / 12))
      : 0;
    const y = rect ? Math.floor((event.clientY - rect.top) / 100) : 0;

    // Create a custom event for component dropped
    const componentDroppedEvent = new CustomEvent('componentDropped', {
      bubbles: true,
      detail: {
        sourceUUID: dragData.uuid,
        containerUUID: targetContainerUUID,
        x,
        y,
        mode: this.currentMode,
      },
    });

    document.dispatchEvent(componentDroppedEvent);
    this.changeDetectorRef.detectChanges();
  }
}
